/** Read-only field projection. Saved source data never becomes an authored claim. */
import type { ChecklistItem } from '../../config/documentChecklists';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';
import type { AuthoredFieldDef } from '../../types/authoredEvidence';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ExtractedClaim } from '../../types/siteContext';
import type { RequirementAutoFill } from './compileCoverageAutoFill';
import { getAlignedFieldPaths } from './requirementFieldAlignment';
import { canPopulateFromTier, isDocOnlyField } from './populateSourcePolicy';
import { resolveReportFields, coverageStatusFraction, type ResolveReportFieldsResult } from './resolveReportFields';
import { formatFieldValue } from '../evidenceLineage/requirementToFields';

function validValue(field: AuthoredFieldDef, value: string | number | undefined): value is string | number {
  if (typeof value === 'number') return field.type === 'number' && Number.isFinite(value);
  if (typeof value !== 'string' || !value.trim() || field.type === 'number' || field.type === 'boolean') return false;
  if (/^(?:unknown|undecided|not (?:yet )?(?:determined|available)|tbd|n\/a)$/i.test(value.trim())) return false;
  return field.type !== 'enum' || Boolean(field.enumValues?.includes(value));
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function rejected(reviews: unknown, key: string): boolean {
  const review = record(reviews) ? reviews[key] : undefined;
  return record(review) && review.decision === 'rejected';
}

function blockedBySourceReview(sed: SiteEvidenceDocument | undefined, requirementId: string, field: AuthoredFieldDef): boolean {
  if (!sed) return false;
  const paths = getAlignedFieldPaths(requirementId, field.key);
  const sections = new Set([...(REQUIREMENT_TO_SECTION_MAP[requirementId] ?? []), ...paths.map(path => path.split('.')[0]!)]);
  const names = new Set([field.key, field.label, ...paths, ...paths.map(path => path.split('.').slice(1).join('.'))].map(name => name.toLowerCase()));
  for (const section of sections) {
    const slot: unknown = sed[section as keyof SiteEvidenceDocument];
    if (!record(slot)) continue;
    const detail = record(slot.provenanceDetail) ? slot.provenanceDetail : undefined;
    if (rejected(detail?.fieldReviews, field.key)) return true;
    for (const path of paths) {
      if (path.startsWith(section + '.') && rejected(detail?.fieldReviews, path.split('.').at(-1)!)) return true;
    }
    if (Array.isArray(slot.docConflicts) && slot.docConflicts.some((conflict: unknown) =>
      record(conflict) && typeof conflict.field === 'string' && names.has(conflict.field.toLowerCase()))) return true;
  }
  return Boolean(sed.evidenceConflicts?.some(conflict => sections.has(conflict.sectionId)
    && names.has(conflict.field.toLowerCase()) && !conflict.autoResolved
    && conflict.userResolution !== 'prefer-provider'));
}

/** Authored, aligned SED and document fields retain their existing precedence.
 * Only empty display rows receive eligible compiled data; document-only fields
 * still need their normal source-bound SED overlay. No input is mutated. */
export function resolveAutomaticReportFields(args: {
  requirement: ChecklistItem;
  sed?: SiteEvidenceDocument;
  authoredClaim?: ExtractedClaim;
  autoFillEntry?: RequirementAutoFill;
}): ResolveReportFieldsResult {
  const resolved = resolveReportFields(args);
  const entry = args.autoFillEntry;
  if (!entry || entry.requirementId !== args.requirement.id || !entry.sourceProvider.trim()) return resolved;
  const schema = new Map(args.requirement.authoredSchema?.map(field => [field.key, field]));
  const primary = resolved.primary.map(row => {
    const field = schema.get(row.key);
    if (!row.isEmpty || !field || field.key === 'notes' || isDocOnlyField(args.requirement.id, field.key)
      || entry.sourceProvider === 'user-doc' || !canPopulateFromTier(args.requirement.id, field.key, 'car')
      || blockedBySourceReview(args.sed, args.requirement.id, field)) return row;
    const value = entry.fields[field.key];
    if (!validValue(field, value)) return row;
    return { ...row, value, displayValue: formatFieldValue(value), isEmpty: false,
      provenance: 'derived' as const, sourceLabel: `${entry.sourceProvider} · automatic source projection` };
  });
  const fraction = coverageStatusFraction({ primary, related: resolved.related });
  return { ...resolved, primary,
    structuredFilled: primary.filter(field => field.key !== 'notes' && !field.isEmpty).length,
    statusFilled: fraction.filled, statusTotal: fraction.total };
}
